import { useMachine } from '@xstate/react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import {
  SuccessNotification,
  SuccessNotificationProps,
} from '../core/screens/SuccessNotification/SuccessNotification';
import { ProviderEntity } from '../models/provider';
import { applicationParameters } from '../utilities/application-parameters';
import { messages as authorizationDirectorMessages } from '../components/AuthorizationDirector/messages';
import { AccessCodeResult } from '../core/components/ConnectAccounts/AccessCodeResult';
import { LoadingOverlay } from '../core/screens/LoadingOverlay/LoadingOverlay';
import { messages as awaitCredentialsMessages } from '../components/screens/AwaitingCredentialsScreen/messages';
import { ReduxCredentials } from '../components/screens/SubmitCredentials/ReduxCredentials';
import { RefreshableItem } from '../models/refreshableItem';
import { createAuthorizeMachine } from './authorizeMachine';

export type AuthorizeMachineViewProps = {
  refreshableItems: Array<RefreshableItem>;
  shouldRedirectEarly: boolean;
  provider: ProviderEntity;
  SuccessNotificationComponent?: React.ComponentType<SuccessNotificationProps>;
};

export const ERROR_CREATING_CREDENTIALS = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_CREATE_CREDENTIALS,
};

// istanbul ignore next: this component is a switch
export const AuthorizeMachineView: React.FC<AuthorizeMachineViewProps> = ({
  refreshableItems,
  shouldRedirectEarly,
  provider,
  SuccessNotificationComponent = SuccessNotification,
}) => {
  const machine = useMemo(() => {
    // This is a bit risky. Application parameters could update
    // after the machine is created. But that would not re-trigger
    // this function.
    const scope = applicationParameters.get('scope');
    const redirectUri = applicationParameters.get('redirectUri');
    const clientId = applicationParameters.get('clientId');
    const state = applicationParameters.get('state');

    return createAuthorizeMachine({
      authorizePayload: {
        scope,
        redirectUri,
        clientId,
        state,
      },
    });
  }, []);

  const [state, send] = useMachine(machine);
  const { authorizeResponse, error, credentials } = state.context;

  if (state.matches('auth')) {
    return (
      <ReduxCredentials
        onBack={undefined}
        refreshableItems={refreshableItems}
        waitForUpdated={!shouldRedirectEarly}
        onDone={(newCredentials) => send('CREDENTIALS', { payload: newCredentials })}
        provider={provider}
      />
    );
  }

  if (state.matches('SUCCESS')) {
    // By this time, we have valid credentials and auth code.
    const applicationSuccessResult = new AccessCodeResult({
      accessCode: authorizeResponse!.code,
      credentialsId: credentials!.id,
    });

    return (
      <SuccessNotificationComponent
        applicationSuccessResult={applicationSuccessResult}
        overlayHeading={
          <FormattedMessage
            {...authorizationDirectorMessages.accountConnectedTo}
            values={{ providerName: provider.displayName }}
          />
        }
      />
    );
  }

  if (state.matches('ERROR')) {
    throw error;
  }

  return (
    <LoadingOverlay
      text={
        <FormattedMessage
          {...awaitCredentialsMessages.connectingTo}
          values={{ providerName: provider.displayName }}
        />
      }
    />
  );
};
